import PropTypes from "prop-types";
import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import articles from "../../backend/db/articles.json";
import SEO from "../SEO";
import styles from "./Article.module.css";

const ArticleContent = ({ title, benefits, conclusion }) => (
  <div className={styles.articleContent}>
    <h2 className={styles.articleContentTitle}>{title}</h2>
    <ul className={styles.benefitsList}>
      {benefits && benefits.map((benefit, index) => (
        <BenefitItem key={index} title={benefit.Title} description={benefit.Description} />
      ))}
    </ul>
    <div className={styles.articleConclusion}>
      <p className={styles.articleConclusionText}>{conclusion}</p>
    </div>
  </div>
);

ArticleContent.propTypes = {
  title: PropTypes.string.isRequired,
  benefits: PropTypes.arrayOf(PropTypes.shape({
    Title: PropTypes.string,
    Description: PropTypes.string,
  })),
  conclusion: PropTypes.string.isRequired,
};

const BenefitItem = ({ title, description }) => (
  <li className={styles.benefitItem}>
    <span className={styles.benefitTitle}>{title}:</span>
    <span className={styles.benefitDescription}>{description}</span>
  </li>
);

BenefitItem.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired
};

const Article = () => {
  const { route } = useParams();
  const article = useMemo(
    () => articles.find((a) => a.Route === route),
    [route]
  );

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!article) {
    return (
      <div className="container">
        <div className={styles.notFoundAlert}>
          <p className={styles.notFoundText}>המאמר לא נמצא</p>
          <Link to="/articles" className={styles.backLink}>
            → חזרה למאמרים
          </Link>
        </div>
      </div>
    );
  }

  const siteUrl = import.meta.env.VITE_SITE_URL || "https://waterless.co.il";

  // Use metaDescription from data or truncate introduction (max ~155 chars)
  const metaDescription = article.metaDescription
    || (article.Introduction.length > 155
      ? article.Introduction.substring(0, 152) + "..."
      : article.Introduction);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.Title,
    description: metaDescription,
    ...(article.publishedDate && { datePublished: article.publishedDate }),
    ...(article.image && { image: `${siteUrl}${article.image}` }),
    author: {
      "@type": "Organization",
      name: article.author || "ווטרלס - Waterless",
    },
    publisher: {
      "@type": "Organization",
      name: "ווטרלס - Waterless",
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/assets/images/waterlessLogo.png`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl}/article/${route}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "דף הבית", item: `${siteUrl}/` },
      { "@type": "ListItem", position: 2, name: "מאמרים", item: `${siteUrl}/articles` },
      {
        "@type": "ListItem",
        position: 3,
        name: article.Title,
        item: `${siteUrl}/article/${route}`,
      },
    ],
  };

  return (
    <div className={styles.articleContainer}>
      <SEO
        title={`${article.Title} | ווטרלס`}
        description={metaDescription}
        path={`/article/${route}`}
        ogType="article"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <div className="container">
        {/* Back link */}
        <Link to="/articles" className={styles.backLink} style={{ marginBottom: '20px', display: 'inline-flex' }}>
          → חזרה למאמרים
        </Link>
        
        {/* Hero Section with Image */}
        {article.image && (
          <div className={styles.articleHero}>
            <img 
              src={article.image} 
              alt={article.Title}
              className={styles.articleHeroImage}
            />
            <div className={styles.articleHeroOverlay}></div>
            <div className={styles.articleHeroContent}>
              <h1 className={styles.articleHeroTitle}>{article.Title}</h1>
              <div className={styles.articleHeroMeta}>
                {formatDate(article.publishedDate)}
                {article.author && ` • ${article.author}`}
              </div>
            </div>
          </div>
        )}
        
        {/* Article Content */}
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <ArticleContent 
              title={article.Introduction} 
              benefits={article.Benefits} 
              conclusion={article.Conclusion} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Article;
